import * as THREE from 'three';
import { Line, Mesh, Object3D } from 'three';

export class GizmoMaterial extends THREE.MeshBasicMaterial {
  oldColor: THREE.Color;
  oldOpacity: number;

  constructor(parameters: THREE.MeshBasicMaterialParameters) {
    super(parameters);

    this.depthTest = false;
    this.depthWrite = false;
    this.side = THREE.FrontSide;
    this.transparent = true;

    this.setValues(parameters);

    this.oldColor = this.color.clone();
    this.oldOpacity = this.opacity;
    this.depthTest = false;
  }

  highlight(highlighted: boolean) {
    if (highlighted) {
      this.color.setRGB(1, 1, 0);
      this.opacity = 1;
    } else {
      this.color.copy(this.oldColor);
      this.opacity = this.oldOpacity;
    }
  }
}

export const pickerMaterial = new GizmoMaterial({ visible: false, transparent: false });

type PickerGizmoData = [Mesh, [number, number, number], [number, number, number]][];
type HandleGizmoData = [Line | Mesh][];

/**
 * The handles are the visual parts of the geometry. For example the circle for the rotation gizmo
 * and the arrows for the translate gizmo.
 *
 * NOTE: The typings are accrurate for rotation gizmo but not for translate gizmo.
 * TODO: Simplify how the handles are stored.
 */
export type HandleGizmos = {
  X: HandleGizmoData;
  Y: HandleGizmoData;
  Z: HandleGizmoData;
  XYZ?: HandleGizmoData;
  XY?: HandleGizmoData;
  YZ?: HandleGizmoData;
  XZ?: HandleGizmoData;
};

/**
 * The pickers are hidden 3d objects which we intersect towards to interract with
 * the gizmos.
 *
 * TODO: Simplify how this data is stored.
 */
export type PickerGizmos = {
  X: PickerGizmoData;
  Y: PickerGizmoData;
  Z: PickerGizmoData;
  XYZ?: PickerGizmoData;
  XY?: PickerGizmoData;
  YZ?: PickerGizmoData;
  XZ?: PickerGizmoData;
};

export class GizmoLineMaterial extends THREE.MeshBasicMaterial {
  linewidth: number;
  oldColor: THREE.Color;
  oldOpacity: number;

  constructor(parameters: THREE.MeshBasicMaterialParameters) {
    super(parameters);

    this.depthTest = false;
    this.depthWrite = false;
    this.transparent = true;
    this.linewidth = 1;

    this.setValues(parameters);

    this.oldColor = this.color.clone();
    this.oldOpacity = this.opacity;
    this.depthTest = false;
  }

  highlight(highlighted: boolean) {
    if (highlighted) {
      this.color.setRGB(1, 1, 0);
      this.opacity = 1;
    } else {
      this.color.copy(this.oldColor);
      this.opacity = this.oldOpacity;
    }
  }
}

class GizmoPlanes extends Object3D {
  XY?: THREE.Mesh;
  YZ?: THREE.Mesh;
  XZ?: THREE.Mesh;
  XYZE?: THREE.Mesh;
}

export class Gizmo extends THREE.Object3D {
  handles?: THREE.Object3D;
  pickers?: THREE.Object3D;
  planes?: GizmoPlanes;
  activePlane?: THREE.Mesh;

  handleGizmos?: HandleGizmos;
  pickerGizmos?: PickerGizmos;

  axis = 'XY';

  init() {
    this.handles = new THREE.Object3D();
    this.pickers = new THREE.Object3D();
    this.planes = new THREE.Object3D();

    this.add(this.handles);
    this.add(this.pickers);
    this.add(this.planes);

    //// PLANES

    const planeGeometry = new THREE.PlaneGeometry(50, 50, 2, 2);
    const planeMaterial = new THREE.MeshBasicMaterial({
      visible: false,
      side: THREE.DoubleSide,
    });

    const planes = {
      XY: new THREE.Mesh(planeGeometry, planeMaterial),
      YZ: new THREE.Mesh(planeGeometry, planeMaterial),
      XZ: new THREE.Mesh(planeGeometry, planeMaterial),
      XYZE: new THREE.Mesh(planeGeometry, planeMaterial),
    };

    this.activePlane = planes.XYZE;

    planes.YZ.rotation.set(0, Math.PI / 2, 0);
    planes.XZ.rotation.set(-Math.PI / 2, 0, 0);
    (planes.XY.name = 'XY'),
      (planes.YZ.name = 'YZ'),
      (planes.XZ.name = 'XZ'),
      (planes.XYZE.name = 'XYZE'),
      (this.planes.XY = planes.XY);
    this.planes.YZ = planes.YZ;
    this.planes.XZ = planes.XZ;
    this.planes.XYZE = planes.XYZE;

    this.planes.add(planes.XY);
    this.planes.add(planes.YZ);
    this.planes.add(planes.XZ);
    this.planes.add(planes.XYZE);

    //// HANDLES AND PICKERS

    const setupGizmos = function (gizmoMap: any, parent: any) {
      for (const name in gizmoMap) {
        for (let i = gizmoMap[name].length; i--; ) {
          const object = gizmoMap[name][i][0];
          const position = gizmoMap[name][i][1];
          const rotation = gizmoMap[name][i][2];

          object.name = name;

          if (position) {
            object.position.set(position[0], position[1], position[2]);
          }
          if (rotation) {
            object.rotation.set(rotation[0], rotation[1], rotation[2]);
          }

          parent.add(object);
        }
      }
    };

    setupGizmos(this.handleGizmos, this.handles);
    setupGizmos(this.pickerGizmos, this.pickers);

    // reset Transformations

    this.traverse(function (child) {
      if (child instanceof THREE.Mesh) {
        child.updateMatrix();

        const tempGeometry = child.geometry.clone();
        tempGeometry.applyMatrix4(child.matrix);
        child.geometry = tempGeometry;

        child.position.set(0, 0, 0);
        child.rotation.set(0, 0, 0);
        child.scale.set(1, 1, 1);
      }
    });
  }

  highlight(axis: string) {
    this.traverse(function (child: any) {
      if (child.material && child.material.highlight) {
        if (child.name === axis) {
          child.material.highlight(true);
        } else {
          child.material.highlight(false);
        }
      }
    });
  }

  update(rotation: THREE.Euler, eye: THREE.Vector3) {
    const vec1 = new THREE.Vector3(0, 0, 0);
    const vec2 = new THREE.Vector3(0, 1, 0);
    const lookAtMatrix = new THREE.Matrix4();

    this.traverse(function (child) {
      if (child.name.search('E') !== -1) {
        child.quaternion.setFromRotationMatrix(lookAtMatrix.lookAt(eye, vec1, vec2));
      } else if (
        child.name.search('X') !== -1 ||
        child.name.search('Y') !== -1 ||
        child.name.search('Z') !== -1
      ) {
        child.quaternion.setFromEuler(rotation);
      }
    });
  }
}
